import { useState, useEffect } from 'react';
import { FileText, Download, TrendingUp, ShoppingBag } from 'lucide-react';
import api from '@/services/api';
import { Order } from '@/types';
import { formatCurrency, formatDate } from '@/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/common/Skeleton';
import toast from '@/hooks/useToast';

type Period = '3m' | '6m' | '1y' | 'all';

const PERIODS: { label: string; value: Period }[] = [
  { label: 'Last 3 months', value: '3m' },
  { label: 'Last 6 months', value: '6m' },
  { label: 'Last year', value: '1y' },
  { label: 'All time', value: 'all' },
];

function getPeriodDates(period: Period): { startDate?: string; endDate?: string } {
  const now = new Date();
  if (period === 'all') return {};
  const start = new Date();
  if (period === '3m') start.setMonth(now.getMonth() - 3);
  if (period === '6m') start.setMonth(now.getMonth() - 6);
  if (period === '1y') start.setFullYear(now.getFullYear() - 1);
  return { startDate: start.toISOString(), endDate: now.toISOString() };
}

export const ExpenseVaultPage = () => {
  const [period, setPeriod] = useState<Period>('1y');
  const [data, setData] = useState<{ orders: Order[]; totalSpend: number; totalOrders: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(getPeriodDates(period) as Record<string, string>);
      const res = await api.get(`/orders/expense-report?${params}`);
      setData(res.data.data);
    } catch { toast.error('Could not load expense data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [period]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ ...getPeriodDates(period) as Record<string, string>, format: 'pdf' });
      const res = await api.get(`/orders/expense-report?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'medical-expenses.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch { toast.error('Could not generate PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
            <FileText className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl text-gray-800">Medical Expense Vault</h1>
            <p className="text-sm text-gray-500">Track spending · Export for insurance or tax claims</p>
          </div>
        </div>
        <Button onClick={downloadPDF} loading={downloading} variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" /> Export PDF
        </Button>
      </div>

      {/* Period selector */}
      <div className="flex gap-2 mb-6">
        {PERIODS.map(p => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              period === p.value ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      {loading ? (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : data && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-card shadow-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-primary-500" />
              <span className="text-sm text-gray-500">Total Medical Spend</span>
            </div>
            <p className="font-heading font-bold text-2xl text-primary-600">{formatCurrency(data.totalSpend)}</p>
          </div>
          <div className="bg-white rounded-card shadow-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingBag className="h-4 w-4 text-accent-500" />
              <span className="text-sm text-gray-500">Total Orders</span>
            </div>
            <p className="font-heading font-bold text-2xl text-gray-800">{data.totalOrders}</p>
          </div>
        </div>
      )}

      {/* Order list */}
      <div className="space-y-3">
        {loading ? Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-20" />) :
          data?.orders.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No paid orders in this period.</div>
          ) : data?.orders.map(order => (
            <div key={order.id} className="bg-white rounded-card shadow-card p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">#{order.orderNumber}</p>
                  <p className="text-xs text-gray-400">{formatDate(order.createdAt)}</p>
                </div>
                <p className="font-bold text-primary-600">{formatCurrency(Number(order.total))}</p>
              </div>
              <div className="text-xs text-gray-500">
                {order.items?.slice(0, 3).map(item => item.product?.name).join(', ')}
                {(order.items?.length || 0) > 3 && ` +${order.items.length - 3} more`}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
};
